import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Upload, FileDown, Loader2, CheckCircle2, AlertTriangle,
  ShieldAlert, HelpCircle, PlayCircle, Building2, RotateCcw, Search,
} from 'lucide-react';
import { contrattiServiziApi, companiesApi } from '../lib/api';
import { toast } from 'sonner';

const MATCH_BADGE = {
  CERTO: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  PROBABILE: 'bg-amber-50 text-amber-700 border-amber-200',
  AMBIGUO: 'bg-orange-50 text-orange-700 border-orange-200',
  NON_TROVATO: 'bg-rose-50 text-rose-700 border-rose-200',
};
const CONTRATTO_BADGE = {
  S: 'bg-sky-50 text-sky-700 border-sky-200',
  M: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'S+M': 'bg-violet-50 text-violet-700 border-violet-200',
  REVISIONE: 'bg-rose-50 text-rose-700 border-rose-200',
};

const ContrattiServiziImportPage = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applyState, setApplyState] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState('ALL');
  // Assegnazioni manuali: {rowNumber: companyId} per righe PROBABILE/AMBIGUO/NON_TROVATO
  const [manualOverrides, setManualOverrides] = useState({});
  // Lista aziende per selettore manuale (caricato una volta)
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await companiesApi.list({ status: 'attive' });
        const arr = Array.isArray(data) ? data : (data?.items || []);
        arr.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'it'));
        setCompanies(arr);
      } catch (e) { /* silent */ }
    })();
  }, []);

  const uploadPreview = async () => {
    if (!file) { toast.error('Seleziona il file ELENCO CONTRATTI E SERVIZI.xlsx'); return; }
    setUploading(true); setPreview(null); setApplyState(null); setApplyResult(null); setSelected(new Set());
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data: init } = await contrattiServiziApi.previewImport(fd);
      const token = init.token;
      let attempts = 0;
      const poll = async () => {
        const { data: st } = await contrattiServiziApi.previewStatus(token);
        setApplyState({ state: st.state, phase: st.phase, progress: st.progress, total: st.total });
        if (st.state === 'done') {
          const { data: rows } = await contrattiServiziApi.previewRows(token);
          setPreview({ token, filename: st.filename, counters: st.counters, total_rows: st.total_rows, elapsed_sec: st.elapsed_sec, rows });
          const c = st.counters || {};
          toast.success(`${st.total_rows} righe · CERTO ${c.CERTO || 0} · PROB ${c.PROBABILE || 0} · AMB ${c.AMBIGUO || 0} · N/T ${c.NON_TROVATO || 0} · REV ${c.REVISIONE_CONTRATTO || 0} ⏱${st.elapsed_sec}s`);
          setUploading(false); setApplyState(null);
          // Preseleziona tutte le righe applicabili (CERTO/PROBABILE con changes)
          const pre = new Set(rows.filter((r) => (r.match_state === 'CERTO' || r.match_state === 'PROBABILE') && r.has_changes).map((r) => r.row_number));
          setSelected(pre);
          return;
        }
        if (st.state === 'error') { toast.error(st.error); setUploading(false); setApplyState(null); return; }
        attempts++;
        if (attempts < 80) setTimeout(poll, 2500);
        else { toast.error('Preview troppo lento'); setUploading(false); setApplyState(null); }
      };
      poll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore preview'); setUploading(false);
    }
  };

  const filteredRows = useMemo(() => {
    if (!preview) return [];
    const rows = preview.rows;
    if (filter === 'ALL') return rows;
    if (filter === 'CHANGES') return rows.filter((r) => r.has_changes);
    if (filter === 'REVISIONE') return rows.filter((r) => r.contratto_computed === 'REVISIONE');
    return rows.filter((r) => r.match_state === filter);
  }, [preview, filter]);

  const toggleRow = (rn) => setSelected((prev) => {
    const nx = new Set(prev); nx.has(rn) ? nx.delete(rn) : nx.add(rn); return nx;
  });
  const toggleVisible = () => {
    const eligible = filteredRows.filter((r) => (r.match_state === 'CERTO' || r.match_state === 'PROBABILE') && r.has_changes);
    const all = eligible.every((r) => selected.has(r.row_number));
    setSelected((prev) => {
      const nx = new Set(prev);
      eligible.forEach((r) => all ? nx.delete(r.row_number) : nx.add(r.row_number));
      return nx;
    });
  };

  const applyNow = async () => {
    if (!preview || selected.size === 0) { toast.error('Nessuna riga selezionata'); return; }
    if (!window.confirm(`Applicare ${selected.size} righe? Verrà creato un backup delle aziende toccate.`)) return;
    setApplyState({ state: 'running', phase: 'starting' });
    try {
      const { data: init } = await contrattiServiziApi.applyImport({
        token: preview.token,
        rowNumbers: Array.from(selected),
        manualOverrides,
      });
      const opId = init.opId;
      const poll = async () => {
        const { data: st } = await contrattiServiziApi.applyStatus(opId);
        setApplyState({ state: st.state, phase: st.phase, progress: st.progress, total: st.total });
        if (st.state === 'done') {
          setApplyResult({ opId, ...st });
          toast.success(`Applicate ${st.applied} righe · Backup: ${st.backup_collection}`);
          setApplyState(null);
          return;
        }
        if (st.state === 'error') { toast.error(st.error); setApplyState(null); return; }
        setTimeout(poll, 2000);
      };
      poll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore applica'); setApplyState(null);
    }
  };

  const doRollback = async () => {
    if (!applyResult) return;
    if (!window.confirm(`Rollback delle modifiche (${applyResult.applied} aziende)? Verranno ripristinati i valori pre-import.`)) return;
    try {
      const { data } = await contrattiServiziApi.rollback(applyResult.opId);
      toast.success(`Ripristinate ${data.restored} aziende`);
      setApplyResult(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore rollback'); }
  };

  const c = preview?.counters || {};

  return (
    <div className="p-4 md:p-6 space-y-5">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-slate-100" data-testid="cs-back">
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-[#023F80]">Contratti e servizi — Import "ELENCO CONTRATTI E SERVIZI"</h1>
          <p className="text-xs text-slate-500">Aggiorna aziende esistenti (foglio ATTIVI). Non crea aziende nuove. Non sovrascrive con celle vuote. Backup automatico + rollback.</p>
        </div>
        <button
          onClick={async () => {
            if (!window.confirm('Simulare la pulizia della tipologia visita (dry-run)?')) return;
            try {
              const { data } = await contrattiServiziApi.cleanupTipologia(true);
              const msg = `DRY RUN — Righe da spostare: ${data.moved}. Vuoi applicare adesso?`;
              if (window.confirm(msg)) {
                const { data: real } = await contrattiServiziApi.cleanupTipologia(false);
                toast.success(`Cleanup applicato: ${real.moved} visite ripulite`);
              }
            } catch (e) { toast.error(e.response?.data?.detail || 'Errore cleanup'); }
          }}
          data-testid="cs-cleanup-tipologia-btn"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-800"
          title="Sposta i valori non canonici da 'tipoVisita' verso 'accertamenti' nelle visite mediche esistenti"
        >
          🧹 Pulisci tipologia visite
        </button>
      </div>

      {/* Uploader */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <div className="flex items-center gap-2">
          <input type="file" accept=".xlsx,.xlsm" onChange={(e) => setFile(e.target.files?.[0] || null)}
                 data-testid="cs-file-input"
                 className="text-xs file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200" />
          <button onClick={uploadPreview} disabled={!file || uploading} data-testid="cs-preview-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#023F80] text-white disabled:opacity-60">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? (applyState?.phase ? `${applyState.phase} ${applyState.progress || 0}/${applyState.total || '?'}` : 'Analizzo…') : 'Anteprima'}
          </button>
          {preview && (
            <button onClick={() => contrattiServiziApi.downloadReport(preview.token)} data-testid="cs-download-report"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200">
              <FileDown size={13} /> Report Excel
            </button>
          )}
        </div>
      </div>

      {/* Counters */}
      {preview && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Counter label="CERTO" value={c.CERTO} tone="emerald" onClick={() => setFilter('CERTO')} active={filter === 'CERTO'} />
          <Counter label="PROBABILE" value={c.PROBABILE} tone="amber" onClick={() => setFilter('PROBABILE')} active={filter === 'PROBABILE'} />
          <Counter label="AMBIGUO" value={c.AMBIGUO} tone="orange" onClick={() => setFilter('AMBIGUO')} active={filter === 'AMBIGUO'} />
          <Counter label="NON TROVATO" value={c.NON_TROVATO} tone="rose" onClick={() => setFilter('NON_TROVATO')} active={filter === 'NON_TROVATO'} />
          <Counter label="REVISIONE contratto" value={c.REVISIONE_CONTRATTO} tone="rose" onClick={() => setFilter('REVISIONE')} active={filter === 'REVISIONE'} />
        </div>
      )}

      {/* Apply result */}
      {applyResult && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="font-semibold text-emerald-900 flex items-center gap-2"><CheckCircle2 size={16}/> Applicazione completata</div>
              <div className="text-emerald-800 text-xs mt-1">
                Applicate <b>{applyResult.applied}</b> aziende · Ambigue saltate: {applyResult.skipped_ambigui} · Nessun match: {applyResult.skipped_no_match} · No changes: {applyResult.skipped_no_changes}
                <br/>Backup: <code>{applyResult.backup_collection}</code>
              </div>
            </div>
            <button onClick={doRollback} data-testid="cs-rollback"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-white border border-emerald-300 text-emerald-800 hover:bg-emerald-100">
              <RotateCcw size={13}/> Rollback
            </button>
          </div>
        </div>
      )}

      {/* Rows table */}
      {preview && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <button onClick={() => setFilter('ALL')} className={`text-xs px-2 py-1 rounded ${filter === 'ALL' ? 'bg-slate-200' : 'hover:bg-slate-100'}`}>Tutte</button>
              <button onClick={() => setFilter('CHANGES')} className={`text-xs px-2 py-1 rounded ${filter === 'CHANGES' ? 'bg-slate-200' : 'hover:bg-slate-100'}`}>Solo con differenze</button>
              <span className="text-xs text-slate-500 ml-2">{filteredRows.length} righe visibili — {selected.size} selezionate</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={toggleVisible} className="text-xs px-2 py-1 rounded hover:bg-slate-100">Toggle visibili applicabili</button>
              <button onClick={applyNow} disabled={selected.size === 0 || !!applyState} data-testid="cs-apply-btn"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-600 text-white disabled:opacity-50">
                <PlayCircle size={14}/> Applica {selected.size} righe
              </button>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[70vh]">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-left text-slate-600">
                  <th className="px-2 py-2 w-10">☑</th>
                  <th className="px-2 py-2 w-12">Riga</th>
                  <th className="px-2 py-2">Azienda (file)</th>
                  <th className="px-2 py-2">Match / DB</th>
                  <th className="px-2 py-2">Contratto</th>
                  <th className="px-2 py-2">Campi in cambio</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => {
                  const hasOverride = !!manualOverrides[r.row_number];
                  const canApplyBase = (r.match_state === 'CERTO' || r.match_state === 'PROBABILE') && r.has_changes;
                  const canApply = canApplyBase || hasOverride;
                  return (
                  <tr key={r.row_number} className="border-t border-slate-100 hover:bg-slate-50 align-top">
                    <td className="px-2 py-2">
                      <input type="checkbox" checked={selected.has(r.row_number)}
                             onChange={() => toggleRow(r.row_number)}
                             disabled={!canApply}
                             data-testid={`cs-check-${r.row_number}`} />
                    </td>
                    <td className="px-2 py-2 font-mono text-slate-400">{r.row_number}</td>
                    <td className="px-2 py-2">
                      <div className="font-semibold text-slate-800">{r.input_azienda}</div>
                      <div className="text-[10px] text-slate-400 truncate max-w-xs">norm: {r.normalized}</div>
                      {r.warnings?.length > 0 && (
                        <div className="mt-1 text-[10px] text-amber-700 flex items-start gap-1"><AlertTriangle size={10} className="mt-0.5"/>{r.warnings.join(' · ')}</div>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-semibold ${MATCH_BADGE[r.match_state] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                        {r.match_state} {r.match_score ? `· ${(r.match_score * 100).toFixed(0)}%` : ''}
                      </span>
                      {r.matched_company && !hasOverride && (
                        <div className="mt-1 text-[11px] text-slate-700"><Building2 size={10} className="inline mr-1"/>{r.matched_company.name}</div>
                      )}
                      {r.candidates?.length > 0 && !hasOverride && (
                        <div className="mt-1">
                          <div className="text-[10px] text-slate-500">{r.candidates.length} candidati — clicca per assegnare:</div>
                          <ul className="text-[10px] text-slate-600 pl-1 mt-1 space-y-0.5">
                            {r.candidates.slice(0, 5).map((cd) => (
                              <li key={cd.id}>
                                <button
                                  onClick={() => setManualOverrides((p) => ({ ...p, [r.row_number]: cd.id }))}
                                  data-testid={`cs-assign-cand-${r.row_number}-${cd.id}`}
                                  className="text-left hover:bg-emerald-50 hover:text-emerald-800 px-1 py-0.5 rounded w-full">
                                  → {cd.name} <span className="text-slate-400">({cd.contratto || '-'})</span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {hasOverride && (
                        <div className="mt-1 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                          <div className="flex items-center justify-between gap-1">
                            <span><Building2 size={10} className="inline mr-1"/>Assegnata manualmente: {companies.find((c) => c.id === manualOverrides[r.row_number])?.name || manualOverrides[r.row_number]}</span>
                            <button onClick={() => setManualOverrides((p) => { const nx = { ...p }; delete nx[r.row_number]; return nx; })}
                                    className="text-rose-600 hover:underline">annulla</button>
                          </div>
                        </div>
                      )}
                      {/* Search picker: sempre disponibile per PROBABILE/AMBIGUO/NON_TROVATO */}
                      {(r.match_state === 'PROBABILE' || r.match_state === 'AMBIGUO' || r.match_state === 'NON_TROVATO') && !hasOverride && (
                        <details className="mt-1">
                          <summary className="text-[10px] text-[#023F80] cursor-pointer hover:underline flex items-center gap-1">
                            <Search size={10}/> Cerca e assegna un'altra azienda
                          </summary>
                          <ManualCompanyPicker companies={companies}
                                                onPick={(cid) => setManualOverrides((p) => ({ ...p, [r.row_number]: cid }))}
                                                testIdPrefix={`cs-search-${r.row_number}`} />
                        </details>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-semibold ${CONTRATTO_BADGE[r.contratto_computed] || 'bg-slate-100'}`}>{r.contratto_computed}</span>
                      <div className="text-[10px] text-slate-500 mt-1">{r.contratto_reason}</div>
                    </td>
                    <td className="px-2 py-2">
                      {r.has_changes ? (
                        <ul className="space-y-0.5">
                          {Object.entries(r.diff).filter(([k]) => k !== 'servizi').map(([k, v]) => (
                            <li key={k} className="text-[11px]">
                              <span className="font-semibold text-slate-600">{k}:</span>{' '}
                              <span className="text-rose-600 line-through">{String(v.old ?? '—').slice(0, 30)}</span>{' → '}
                              <span className="text-emerald-700 font-semibold">{String(v.new).slice(0, 40)}</span>
                            </li>
                          ))}
                          {r.diff.servizi && (
                            <li className="text-[11px]">
                              <span className="font-semibold text-slate-600">servizi:</span>{' '}
                              <span className="text-emerald-700">{Object.keys(r.diff.servizi).join(', ')}</span>
                            </li>
                          )}
                        </ul>
                      ) : (
                        <span className="text-slate-400 text-[11px]">— nessuna differenza —</span>
                      )}
                    </td>
                  </tr>
                  );
                })}
                {filteredRows.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-slate-400 py-8 text-sm">Nessuna riga per il filtro selezionato</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const ManualCompanyPicker = ({ companies, onPick, testIdPrefix }) => {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s || s.length < 2) return [];
    return companies.filter((c) => (c.name || '').toLowerCase().includes(s)).slice(0, 8);
  }, [companies, q]);
  return (
    <div className="mt-1 space-y-1">
      <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
             placeholder="digita almeno 2 caratteri del nome…"
             data-testid={`${testIdPrefix}-input`}
             className="w-full text-xs px-2 py-1 rounded border border-slate-200 focus:outline-none focus:ring-1 focus:ring-[#023F80]" />
      {filtered.length > 0 && (
        <ul className="text-[10px] space-y-0.5 max-h-40 overflow-y-auto">
          {filtered.map((c) => (
            <li key={c.id}>
              <button onClick={() => onPick(c.id)}
                      data-testid={`${testIdPrefix}-pick-${c.id}`}
                      className="text-left hover:bg-emerald-50 hover:text-emerald-800 px-1 py-0.5 rounded w-full">
                → {c.name} <span className="text-slate-400">({c.contratto || '-'})</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Counter = ({ label, value, tone, onClick, active }) => {
  const tones = {
    emerald: 'from-emerald-50 to-emerald-100/40 border-emerald-200 text-emerald-800',
    amber: 'from-amber-50 to-amber-100/40 border-amber-200 text-amber-800',
    orange: 'from-orange-50 to-orange-100/40 border-orange-200 text-orange-800',
    rose: 'from-rose-50 to-rose-100/40 border-rose-200 text-rose-800',
  };
  return (
    <button onClick={onClick}
            className={`text-left rounded-xl border bg-gradient-to-br p-3 shadow-sm hover:shadow transition ${tones[tone]} ${active ? 'ring-2 ring-offset-1 ring-slate-400' : ''}`}
            data-testid={`cs-counter-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="text-[10px] uppercase font-semibold tracking-wide opacity-70">{label}</div>
      <div className="text-2xl font-bold">{value ?? 0}</div>
    </button>
  );
};

export default ContrattiServiziImportPage;
